import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Brain, GraduationCap, Thermometer, MessageSquare, LogOut, ShieldCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { KlachtenreglementFooterLink } from './klachten/KlachtenreglementModal';
import { VeiligeGesprekkenNode } from './veiligeHaven/VeiligeGesprekken';

interface Tab {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  label: string;
}

/**
 * Schil van het anonieme Medewerkersportaal (mobile-first).
 * Bovenbalk met wordmark + pseudoniem, vaste tabbalk onderin met de 3 pijlers + home + berichten (opleiders).
 * De Academy draait in "theatermodus": breed (Netflix-catalogus), in de lichte huisstijl.
 * De startpagina (De Veilige Haven, onderdeel 60) is breed genoeg voor het 4-vensterblok;
 * rechtsonder zweeft op elke pagina de chat-console "Mijn Veilige Gesprekken".
 */
export const MedewerkerLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('medewerker');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const pseudoniem = user?.last_name ?? '';
  const pad = useLocation().pathname;
  const theater = pad.startsWith('/medewerker/academy');
  const breedte = theater ? 'max-w-7xl' : pad === '/medewerker' ? 'max-w-5xl' : pad === '/medewerker/koffer' ? 'max-w-2xl' : 'max-w-md';

  const tabs: Tab[] = [
    { to: '/medewerker', end: true, icon: Home, label: t('nav.home', { defaultValue: 'Home' }) },
    { to: '/medewerker/professionals', icon: Brain, label: t('nav.professionals', { defaultValue: 'Professionals' }) },
    { to: '/medewerker/academy', icon: GraduationCap, label: t('nav.academy', { defaultValue: 'Academy' }) },
    { to: '/medewerker/thermometer', icon: Thermometer, label: t('nav.thermometer', { defaultValue: 'Thermometer' }) },
    { to: '/medewerker/berichten', icon: MessageSquare, label: t('nav.berichten', { defaultValue: 'Berichten' }) },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`min-h-screen flex flex-col bg-[#F7F5F0]`}>
      <header
        className={`sticky top-0 z-20 border-b bg-white border-gray-200`}
      >
        <div className={`${breedte} mx-auto px-4 h-14 flex items-center justify-between`}>
          <img src="/belagom-logo.svg" alt="BeLagom" className="h-7 w-auto" />
          <div className="flex items-center gap-2">
            {pseudoniem && (
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-mono font-medium bg-blue-50 text-[#0B1F3A] border-blue-100`}
                title={t('layout.pseudoniemTitel', { defaultValue: 'Jouw anonieme code' })}
              >
                {pseudoniem}
              </span>
            )}
            <button
              type="button"
              onClick={handleLogout}
              aria-label={t('layout.uitloggen', { defaultValue: 'Uitloggen' })}
              className={`p-2 rounded-lg transition-colors text-gray-500 hover:bg-gray-100 hover:text-gray-900`}
            >
              <LogOut className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <main className={`flex-1 w-full ${breedte} mx-auto px-4 py-5 pb-28`}>
        {children}
        <p className={`mt-10 text-center text-xs flex items-start justify-center gap-1.5 px-2 text-gray-500`}>
          <ShieldCheck className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            {t('layout.anoniemFooter', {
              defaultValue: 'Anoniem — je werkgever ziet alleen je code, nooit je naam of e-mail.',
            })}
          </span>
        </p>
        <div className="mt-3 flex justify-center">
          <KlachtenreglementFooterLink className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-navy-900 hover:underline" />
        </div>
      </main>

      <VeiligeGesprekkenNode rol="medewerker" />

      <nav
        className={`fixed bottom-0 inset-x-0 z-20 border-t pb-[env(safe-area-inset-bottom)] bg-white border-gray-200`}
        aria-label={t('layout.hoofdnavigatie', { defaultValue: 'Hoofdnavigatie' })}
      >
        <div className="max-w-md mx-auto grid grid-cols-5">
          {tabs.map(({ to, end, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                  isActive ? 'text-navy-900' : 'text-gray-500 hover:text-gray-800'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`rounded-full px-3 py-0.5 ${isActive ? 'bg-gold-100' : ''}`}>
                    <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} aria-hidden="true" />
                  </span>
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default MedewerkerLayout;
